import {
  IDocumentRelation,
  IRelatedDocumentRole,
  IRelatedDocumentSummary,
  IRelationType
} from '@query/documentRelations'
import { IDocument } from '@query/documents'

export type AccrualLeadState = 'full' | 'partial' | 'over'

const roundMoney = (amount: number): number => Math.round(amount * 100) / 100

const nonNegativeRemainder = (total: number, applied: number): number =>
  Math.max(0, roundMoney(total - applied))

const ACCRUAL_LEAD_KEYS: Record<AccrualLeadState, string> = {
  full: 'documentRelations.lead.ACCRUAL.full',
  partial: 'documentRelations.lead.ACCRUAL.partial',
  over: 'documentRelations.lead.ACCRUAL.over'
}

const SALARY_PAYMENT_ACCRUAL_LEAD_KEYS: Record<AccrualLeadState, string> = {
  full: 'documentRelations.lead.SALARY_PAYMENT.ACCRUAL.full',
  partial: 'documentRelations.lead.SALARY_PAYMENT.ACCRUAL.partial',
  over: 'documentRelations.lead.SALARY_PAYMENT.ACCRUAL.over'
}

/**
 * Display amount for a document in relation UI.
 * Matches backend `get_related_document_display_amount`: prefer abs(due effect)
 * when non-zero; otherwise fall back to balance. Zero due effect is common on
 * settlements (no net due entries) and must not win over balance.
 */
export const getDocumentAmountForRelation = (
  document?: Partial<Pick<IDocument, 'balance' | 'relation_entry_signals'>> | null
): number | null => {
  const dueEffect = document?.relation_entry_signals?.due_account_effect
  if (dueEffect != null && dueEffect !== 0) {
    return Math.abs(dueEffect)
  }

  if (document?.balance != null) {
    return Math.abs(document.balance)
  }

  return null
}

export const buildRelatedDocumentSummary = (document: IDocument): IRelatedDocumentSummary => {
  const amount = getDocumentAmountForRelation(document) ?? 0

  return {
    id: document.id,
    number: document.number ?? '',
    date: document.date ?? null,
    contact_name: document.contact_name ?? null,
    amount
  }
}

export const getRelationRowAmount = (
  relation: Pick<IDocumentRelation, 'related_document' | 'related_document_summary'>,
  relatedDocument?: Pick<IDocument, 'balance' | 'relation_entry_signals'> | null
): number | null => {
  const summaryAmount = relation.related_document_summary?.amount
  if (summaryAmount != null) {
    return Math.abs(summaryAmount)
  }

  return getDocumentAmountForRelation(relatedDocument)
}

export const sumRelationAmounts = (
  relations: Array<Pick<IDocumentRelation, 'related_document' | 'related_document_summary'>>,
  relatedDocumentsById: Record<
    number,
    Pick<IDocument, 'balance' | 'relation_entry_signals'> | undefined
  > = {}
): number =>
  relations.reduce((total, relation) => {
    const amount = getRelationRowAmount(relation, relatedDocumentsById[relation.related_document])
    return total + (amount ?? 0)
  }, 0)

/**
 * Footer / mismatch amounts for the relation mapper.
 * Prefer backend ACCRUAL_PAIR network totals; fall back to local linked sums
 * only while relations are unsaved (optimistic) or totals are not yet available.
 */
export const getRelationMapperDisplayAmounts = ({
  relations,
  role,
  sourceDocumentAmount,
  dueAccountEffect,
  accrualPairDueEffect,
  hasAccrualPairDueMismatch,
  accrualPairInvoiceTotal,
  accrualPairPaymentTotal,
  relatedDocumentsById = {}
}: {
  relations: Array<
    Pick<IDocumentRelation, 'related_document' | 'related_document_summary' | 'role'> & {
      id?: number | null
    }
  >
  /** Role of the open document; drives payment vs invoice open-amount math. */
  role?: IRelatedDocumentRole | null
  sourceDocumentAmount?: number | null
  dueAccountEffect?: number | null
  accrualPairDueEffect?: number | null
  hasAccrualPairDueMismatch?: boolean
  accrualPairInvoiceTotal?: number | null
  accrualPairPaymentTotal?: number | null
  relatedDocumentsById?: Record<
    number,
    Pick<IDocument, 'balance' | 'relation_entry_signals'> | undefined
  >
}): {
  sourceAmount: number | null
  networkInvoiceTotal: number | null
  networkPaymentTotal: number | null
  liveOpenAmount: number | null
  hasMismatch: boolean
} => {
  const hasUnsavedRelations = relations.some(relation => relation.id == null)
  const hasMismatch = !hasUnsavedRelations && (hasAccrualPairDueMismatch ?? false)

  const hasNetworkTotals =
    !hasUnsavedRelations && accrualPairInvoiceTotal != null && accrualPairPaymentTotal != null

  if (hasNetworkTotals) {
    return {
      sourceAmount: accrualPairInvoiceTotal,
      networkInvoiceTotal: accrualPairInvoiceTotal,
      networkPaymentTotal: accrualPairPaymentTotal,
      liveOpenAmount: nonNegativeRemainder(accrualPairInvoiceTotal, accrualPairPaymentTotal),
      hasMismatch
    }
  }

  // Optimistic / no network totals yet: local document amount for role fallbacks.
  let sourceAmount: number | null = null
  if (dueAccountEffect != null && dueAccountEffect !== 0) {
    sourceAmount = Math.abs(dueAccountEffect)
  } else if (sourceDocumentAmount != null) {
    sourceAmount = sourceDocumentAmount
  }

  const linkedTotal = sumRelationAmounts(relations, relatedDocumentsById)
  const normalizedOpenAmount = accrualPairDueEffect != null ? Math.abs(accrualPairDueEffect) : null
  const resolvedRole = role ?? relations[0]?.role ?? null

  // Invoice view: open = invoice − linked payments.
  // Payment view: open = linked invoices − this payment (not the reverse).
  let liveOpenAmount: number | null = normalizedOpenAmount
  if (resolvedRole === IRelatedDocumentRole.SETTLEMENT) {
    if (linkedTotal > 0 && sourceAmount != null) {
      liveOpenAmount = nonNegativeRemainder(linkedTotal, sourceAmount)
    }
  } else if (sourceAmount != null && sourceAmount > 0) {
    liveOpenAmount = nonNegativeRemainder(sourceAmount, linkedTotal)
  }

  return {
    sourceAmount,
    networkInvoiceTotal: null,
    networkPaymentTotal: null,
    liveOpenAmount,
    hasMismatch
  }
}

/** Footer totals: backend network totals when present, otherwise role-local fallback. */
export const getRelationFooterAmounts = ({
  role,
  sourceAmount,
  linkedTotal,
  fallbackOpenAmount,
  networkInvoiceTotal,
  networkPaymentTotal
}: {
  role: IRelatedDocumentRole
  sourceAmount: number | null
  linkedTotal: number
  fallbackOpenAmount: number | null
  networkInvoiceTotal?: number | null
  networkPaymentTotal?: number | null
}): { appliedAmount: number; targetAmount: number | null; liveOpenAmount: number | null } => {
  if (networkInvoiceTotal != null && networkPaymentTotal != null) {
    return {
      appliedAmount: networkPaymentTotal,
      targetAmount: networkInvoiceTotal,
      liveOpenAmount: nonNegativeRemainder(networkInvoiceTotal, networkPaymentTotal)
    }
  }

  if (role === IRelatedDocumentRole.SETTLEMENT) {
    const targetAmount = linkedTotal > 0 ? linkedTotal : null
    const liveOpenAmount =
      targetAmount != null && sourceAmount != null && targetAmount > 0
        ? nonNegativeRemainder(targetAmount, sourceAmount)
        : fallbackOpenAmount
    const appliedAmount =
      targetAmount != null && liveOpenAmount != null
        ? nonNegativeRemainder(targetAmount, liveOpenAmount)
        : sourceAmount ?? 0
    return { appliedAmount, targetAmount, liveOpenAmount }
  }

  const targetAmount = sourceAmount
  const appliedAmount = linkedTotal
  const liveOpenAmount =
    targetAmount != null && targetAmount > 0
      ? nonNegativeRemainder(targetAmount, appliedAmount)
      : fallbackOpenAmount

  return { appliedAmount, targetAmount, liveOpenAmount }
}

export const getAccrualLeadState = ({
  status,
  appliedAmount,
  targetAmount
}: {
  status: 'settled' | 'partial' | 'mismatch' | 'none'
  appliedAmount: number
  targetAmount: number | null
}): AccrualLeadState => {
  const roundedApplied = roundMoney(appliedAmount)
  const roundedTarget = targetAmount == null ? null : roundMoney(targetAmount)

  if (roundedTarget != null && roundedApplied > roundedTarget) {
    return 'over'
  }

  if (status === 'partial') {
    return 'partial'
  }

  if (status === 'mismatch' && roundedTarget != null && roundedApplied < roundedTarget) {
    return 'partial'
  }

  if (
    status === 'none' &&
    roundedApplied > 0 &&
    roundedTarget != null &&
    roundedApplied < roundedTarget
  ) {
    return 'partial'
  }

  return 'full'
}

export const getLeadSentenceKey = (
  role: IRelatedDocumentRole,
  options: { leadState?: AccrualLeadState; relationType?: IRelationType } = {}
): string => {
  const relationType = options.relationType ?? IRelationType.ACCRUAL_PAIR
  const leadState = options.leadState ?? 'full'

  switch (relationType) {
    case IRelationType.SALARY_PAYMENT:
      if (role === IRelatedDocumentRole.SETTLEMENT) {
        return 'documentRelations.lead.SALARY_PAYMENT.SETTLEMENT'
      }
      return SALARY_PAYMENT_ACCRUAL_LEAD_KEYS[leadState]
    case IRelationType.ACCRUAL_PAIR:
      if (role === IRelatedDocumentRole.SETTLEMENT) {
        return 'documentRelations.lead.SETTLEMENT'
      }
      return ACCRUAL_LEAD_KEYS[leadState]
    default: {
      const exhaustive: never = relationType
      return exhaustive
    }
  }
}

export const getSettlementStatus = ({
  invoiceAmount,
  paidAmount,
  openAmount,
  hasMismatch
}: {
  invoiceAmount: number | null
  paidAmount: number
  openAmount: number | null
  hasMismatch: boolean
}): 'settled' | 'partial' | 'mismatch' | 'none' => {
  if (hasMismatch) {
    return 'mismatch'
  }

  if (invoiceAmount == null || invoiceAmount <= 0) {
    if (openAmount != null && openAmount <= 0 && paidAmount > 0) {
      return 'settled'
    }
    return paidAmount > 0 ? 'partial' : 'none'
  }

  if (openAmount != null && openAmount > 0) {
    return 'partial'
  }

  if (paidAmount >= invoiceAmount) {
    return 'settled'
  }

  if (paidAmount > 0) {
    return 'partial'
  }

  return 'none'
}
